import { create } from 'zustand';
import { api } from '@/lib/api/client';
import { AppError } from '@/utils/errors/AppError';

interface WhatsAppSettings {
  whatsappNumber: string;
  whatsappMessage: string;
}

interface SettingsState {
  settings: WhatsAppSettings;
  isLoading: boolean;
  error: string | null;
  hasInitialFetch: boolean;
  fetchSettings: () => Promise<void>;
  updateSettings: (settings: Partial<WhatsAppSettings>) => Promise<void>;
  reset: () => void;
}

const DEFAULT_SETTINGS: WhatsAppSettings = {
  whatsappNumber: '',
  whatsappMessage: ''
};

export const useSettingsStore = create<SettingsState>()((set) => ({
  settings: DEFAULT_SETTINGS,
  isLoading: false,
  error: null,
  hasInitialFetch: false,

  fetchSettings: async () => {
    set(state => ({
      ...state,
      isLoading: !state.hasInitialFetch,
      error: null
    }));

    try {
      const response = await api.get<WhatsAppSettings>('api/settings/whatsapp');
      set({
        settings: response,
        isLoading: false,
        error: null,
        hasInitialFetch: true
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      set({
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to fetch settings',
        hasInitialFetch: true
      });
    }
  },

  updateSettings: async (newSettings) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<WhatsAppSettings>('api/settings/whatsapp', newSettings);
      set({ settings: response, isLoading: false, error: null });
    } catch (error) {
      console.error('Failed to update settings:', error);
      set({
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to update settings'
      });
      throw error;
    }
  },

  reset: () => {
    set({
      settings: DEFAULT_SETTINGS,
      isLoading: false,
      error: null,
      hasInitialFetch: false
    });
  }
}));